import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Modal, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useAgentsStore } from "../../../../../../../../../../../store/agents.store";
import {
    selectAllStates,
    selectDraftLicensedStates,
    selectLicensedStates,
} from "../../../../../../../../../../../store/agentDetail/agentDetailSelectors";
import { AgentDetailSectionEnum, IState } from "../../../../../../../../../../../agentDetail.model";
import { AGENT_LICENSED_STATES_SAVE_SCOPE } from "../../../../../../../../../../../agents.constants";
import styles from "./editState.module.css";
import { Button as MuiButton } from "@mui/material";

interface EditStateProps {
    open: boolean;
    onCancel: () => void;
}

export const EditState = ({ open, onCancel }: EditStateProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const initialSelectedIdsRef = useRef<Set<number>>(new Set());

    const allStates = useAgentsStore(selectAllStates);
    const allStatesLoading = useAgentsStore((state) => state.allStatesLoading);
    const draftLicensedStates = useAgentsStore(selectDraftLicensedStates);
    const serverLicensedStates = useAgentsStore(selectLicensedStates);
    const draftLicensedStatesRef = useRef(draftLicensedStates);
    const serverLicensedStatesRef = useRef(serverLicensedStates);
    draftLicensedStatesRef.current = draftLicensedStates;
    serverLicensedStatesRef.current = serverLicensedStates;

    const updateDraftField = useAgentsStore((state) => state.updateDraftField);
    const saveAgentDetail = useAgentsStore((state) => state.saveAgentDetail);

    useEffect(() => {
        if (open) {
            setSelectedIds(new Set(draftLicensedStatesRef.current.map((s: { stateId: number }) => s.stateId)));
            initialSelectedIdsRef.current = new Set(
                serverLicensedStatesRef.current.map((s: { stateId: number }) => s.stateId)
            );
            setSearchQuery("");
        }
    }, [open]);

    const sortedStates = useMemo(() => {
        if (!allStates) return [];
        return [...allStates].sort((a, b) => (a.stateTitle ?? '').localeCompare(b.stateTitle ?? ''));
    }, [allStates]);

    const filteredStates = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return sortedStates;
        return sortedStates.filter(
            (state: IState) =>
                state.stateTitle?.toLowerCase().includes(query) ||
                state.stateCode?.toLowerCase().includes(query)
        );
    }, [searchQuery, sortedStates]);

    const isDirty = useMemo(() => {
        const initial = initialSelectedIdsRef.current;
        if (selectedIds.size !== initial.size) return true;
        for (const id of Array.from(selectedIds)) {
            if (!initial.has(id)) return true;
        }
        return false;
    }, [selectedIds]);

    const handleToggle = useCallback((stateId: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(stateId)) {
                next.delete(stateId);
            } else {
                next.add(stateId);
            }
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedIds(new Set(sortedStates.map((s: IState) => s.stateId)));
    }, [sortedStates]);

    const handleDeselectAll = useCallback(() => {
        setSelectedIds(new Set<number>());
    }, []);

    const handleSave = useCallback(() => {
        const nextStates = sortedStates
            .filter((s: IState) => selectedIds.has(s.stateId))
            .map((s: IState) => ({
                stateId: s.stateId,
                stateName: s.stateTitle ?? "",
            }));
        updateDraftField(
            AgentDetailSectionEnum.CONTRACT_AND_LICENSING_INFORMATION,
            "licensedStates",
            nextStates
        );
        void saveAgentDetail(AGENT_LICENSED_STATES_SAVE_SCOPE);
        onCancel();
    }, [sortedStates, selectedIds, updateDraftField, saveAgentDetail, onCancel]);

    const renderHeader = useMemo(() => (
        <div className={styles.headerCtn}>
            <h2 className={styles.title}>Edit State Licenses</h2>
            <h6 className={styles.description}>Select every state this agent is licensed to sell in.</h6>
        </div>
    ), []);

    const allSelected = sortedStates.length > 0 && selectedIds.size === sortedStates.length;
    const noneSelected = selectedIds.size === 0;

    const deselectAllBtnStyle = useMemo(() => ({
        color: "#ed6c02",
        border: "1px solid #ed6c02",
        "&:hover": {
            border: "1px solid #ed6c02",
            backgroundColor: "rgba(237, 108, 2, 0.1)",
        },
        "&:active": {
            border: "1px solid #e65100",
            backgroundColor: "rgba(237, 108, 2, 0.2)",
        },
    }), []);

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            title={renderHeader}
            footer={
                <div className={styles.footerCtn}>
                    <Button variant="outlined" color="default" onClick={onCancel}>Cancel</Button>
                    <Button variant="solid" color="primary" onClick={handleSave} disabled={!isDirty}>Save Licenses </Button>
                </div>
            }
            centered
            width={800}
            zIndex={1200}
            mask={{ closable: false }}
            keyboard={true}
            closable={true}
            classNames={{
                container: styles.container,
                header: styles.header,
                body: styles.body,
                footer: styles.footer,
            }}
        >
            <div className={styles.bodyCtn}>
                <div className={styles.bodyHeader}>
                    <div className={styles.searchCtn}>
                        <Input
                            placeholder="Search by state name or abbreviation"
                            prefix={<SearchOutlined />}
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            allowClear
                        />
                    </div>
                    <div className={styles.buttonsCtn}>
                        <MuiButton variant="outlined" color="success" onClick={handleSelectAll} className={styles.selectAllBtn} disabled={allSelected}>Select All</MuiButton>
                        <MuiButton
                            variant="outlined"
                            color="warning"
                            onClick={handleDeselectAll}
                            className={styles.clearAllBtn}
                            sx={deselectAllBtnStyle}
                            disabled={noneSelected}
                        >
                            Deselect All
                        </MuiButton>
                    </div>
                </div>
                <div className={styles.stateCount}>
                    <h6 className={styles.stateCountTitle}>{selectedIds.size} of {sortedStates.length} selected</h6>
                </div>
                {allStatesLoading ? (
                    <div className={styles.loadingCtn}>
                        <Spin />
                    </div>
                ) : (
                    <div className={styles.statesCtn}>
                        {filteredStates.map((state) => {
                            const isSelected = selectedIds.has(state.stateId);
                            return (
                                <button
                                    className={`${styles.stateCtn} ${isSelected ? styles.selected : ''}`}
                                    key={state.stateId}
                                    onClick={() => handleToggle(state.stateId)}
                                >
                                    <span className={styles.stateName}>{state.stateTitle}</span>
                                    <span className={styles.stateCode}>{state.stateCode}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </Modal>
    );
};
